import { useMemo, useState, type CSSProperties } from "react"
import Box from "@mui/material/Box"
import { CircularProgress, Stack, Typography } from "@mui/material"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import { useTranslation } from "react-i18next"

import type { ReactNode } from "react"

import type { EntityType } from "types/common.types"
import type { RecordType, QueryPlotDef, StaticPlotDef } from "types/dashboard.types"
import type { QualityRecord, FieldQualityMap } from "types/quality.types"

import { PLOT_CARD_PADDING_BOTTOM } from "helpers/utils/constants"
import useDateRange from "helpers/hooks/useDateRange"
import { getPlotName } from "helpers/utils/translations"
import { formatPlotTitleTooltip, getPlotLoadingKey } from "helpers/utils/plots"
import SectionTitle from "widgets/common/SectionTitle"
import PopUp from "widgets/common/PopUp"
import { PlotContainer } from "../styled/containers"
import GeoPlot from "./geo/GeoPlot"
import SingleStat from "./SingleStat"
import MultiValueStat from "./MultiValueStat"
import TableStat from "./tableStat"
import MuiPieChart from "./mui/MuiPieChart"
import MuiGaugeChart from "./mui/gauge/MuiGaugeChart"
import PlotCsvDownload from "./PlotCsvDownload"
import MuiLineChart from "./mui/MuiLineChart"
import PlotHelpTooltip from "./PlotHelpTooltip"
import PlotDialogIcon from "./PlotDialogIcon"
import PlotDataProcessingTooltip from "./PlotDataProcessingTooltip"
import MuiScatterChart from "./mui/scatter/MuiScatterChart"
import MuiBarChart from "./mui/MuiBarChart"
import WeatherServiceTooltipIcon from "./WeatherServiceTooltipIcon"
import WeatherServiceAttribution from "./WeatherServiceAttribution"
import QualityMiniPie from "./mui/QualityMiniPie"
import QualityErrorIcon from "./mui/QualityErrorIcon"
import PlotRetryChip from "./PlotRetryChip"

interface PlotProps {
  def: QueryPlotDef | StaticPlotDef
  data: RecordType[]
  showUnit?: boolean
  styles?: CSSProperties
  csvData?: {
    entity: EntityType
    entityIds: number[]
    fromDate: string
    toDate: string
  }
  tooltipData?: RecordType[]
  entityData?: { entity: EntityType; ids: number[] }
  qualityData?: QualityRecord[]
  qualityError?: unknown | null
  fromDate?: number
  toDate?: number
  isRawData?: boolean
  seriesLegend?: JSX.Element
  fieldQualityMap?: FieldQualityMap
  subtitle?: ReactNode
  error?: unknown | null
  refetch?: () => void
  isFetching?: boolean
}

export default function Plot({
  def,
  data,
  styles,
  showUnit = false,
  csvData,
  tooltipData = [],
  entityData,
  qualityData,
  qualityError,
  fromDate,
  toDate,
  isRawData,
  seriesLegend,
  fieldQualityMap,
  subtitle,
  error,
  refetch,
  isFetching = false,
}: PlotProps) {
  const { view, downloadable, draggable } = def

  const { t, i18n } = useTranslation()
  const { getPlotLoadingState } = useDateRange()
  const reqConfig = "reqConfig" in def ? def.reqConfig : undefined
  const { fields, aggregate, method } = reqConfig ?? {}

  const dataSource = "dataSource" in def ? def.dataSource : undefined
  const usesWeatherService = dataSource?.service === "weather"
  const attribution = "attribution" in def ? def.attribution : undefined

  // Use "weatherService" as entity if weatherServiceRelated, otherwise use the entity prop
  const plotEntity = usesWeatherService ? "weatherService" : entityData?.entity

  const plotLoadingKey = getPlotLoadingKey({
    fields,
    aggregate: isRawData ? undefined : aggregate,
    method,
    entity: plotEntity,
  })
  const isPlotLoading = getPlotLoadingState(plotLoadingKey)
  const [showDialog, setShowDialog] = useState(false)

  const { nameKey, unit, tooltip } = getPlotName(view, showUnit)
  const tooltipContent = useMemo(
    () =>
      tooltip ? formatPlotTitleTooltip(tooltip, t, i18n.resolvedLanguage) : undefined,
    [t, tooltip, i18n.resolvedLanguage],
  )

  const plotTitle = t([`plot_titles.${nameKey}`, view.name])

  const hasFailed = Boolean(error)
  const hasPartialError = hasFailed && data.length > 0
  const isFullFailure = hasFailed && !hasPartialError
  const isRetrying = hasFailed && isFetching

  // A mounted plot with no data (e.g. a tabbed plot whose active tab has no points
  // in the selected range) shows an intentional empty state instead of a blank chart.
  const isEmptyInRange =
    !hasFailed && data.length === 0 && (view.type === "xy" || view.type === "scatter")

  const showQualityError = Boolean(qualityError) && !(hasFailed && !hasPartialError)

  const qualityDescriptionParams = useMemo(() => {
    if (!qualityData || !entityData) return undefined
    const entityKey =
      entityData.ids.length > 1
        ? `entities.${entityData.entity.toUpperCase()}S`
        : `entities.${entityData.entity.toUpperCase()}`
    const entityLabel = t(entityKey)
    const ids = entityData.ids.join(", ")
    return {
      plotTitle,
      entity: entityLabel,
      entityIds: ids,
    }
  }, [qualityData, entityData, plotTitle, t])

  const handleRefetch = () => {
    refetch?.()
  }

  const handleOpenDialog = () => setShowDialog(true)
  const handleCloseDialog = () => setShowDialog(false)

  const InnerPlot = () => {
    const type = view.type
    switch (type) {
      case "geo":
        return <GeoPlot data={data} />
      case "single-stat":
        return <SingleStat view={view} data={data} />
      case "multi-value-stat":
        return <MultiValueStat view={view} data={data} />
      case "table-stat":
        return <TableStat view={view} data={data} />
      case "gauge":
        return <MuiGaugeChart view={view} data={data} />
      case "pie":
        return <MuiPieChart view={view} data={data} />
      case "scatter":
        return (
          <MuiScatterChart
            view={view}
            data={data}
            fromDate={fromDate}
            toDate={toDate}
            fieldQualityMap={fieldQualityMap}
          />
        )
      case "bar":
        return <MuiBarChart view={view} />
      default:
        return (
          <MuiLineChart
            view={view}
            data={data}
            tooltipData={tooltipData}
            fromDate={fromDate}
            toDate={toDate}
            fieldQualityMap={fieldQualityMap}
          />
        )
    }
  }

  return (
    <>
      {view && (
        <>
          <PlotContainer
            maxWidth="xl"
            type={view.type}
            sx={{
              ...styles,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              borderColor: (theme) =>
                hasFailed
                  ? hasPartialError
                    ? theme.palette.warning.main
                    : theme.palette.error.main
                  : theme.palette.neutral[400],
              pb: view.type === "geo" ? 0 : PLOT_CARD_PADDING_BOTTOM,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                mb: 1,
                minWidth: 0,
                flexWrap: "nowrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SectionTitle
                  title={`${plotTitle}${showUnit && unit ? ` ${unit}` : ""}`}
                  startIcon={
                    usesWeatherService ? <WeatherServiceTooltipIcon /> : undefined
                  }
                  endIcon={
                    <Stack direction="row" spacing={2} alignItems="center">
                      {isPlotLoading || isFetching ? (
                        <CircularProgress size={20} />
                      ) : undefined}
                      {hasFailed && (
                        <PlotRetryChip
                          hasPartialError={hasPartialError}
                          isRetrying={isRetrying}
                          onClick={handleRefetch}
                        />
                      )}
                    </Stack>
                  }
                  subtitle={
                    subtitle ??
                    (!hasFailed && (view.type === "xy" || view.type === "scatter") && (
                      <PlotDataProcessingTooltip
                        isRawData={isRawData}
                        aggregate={aggregate}
                      />
                    ))
                  }
                >
                  {tooltipContent && <PlotHelpTooltip content={tooltipContent} />}
                </SectionTitle>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                {showQualityError ? (
                  <QualityErrorIcon onRetry={refetch} isRetrying={isFetching} />
                ) : (
                  qualityData && (
                    <QualityMiniPie
                      qualityData={qualityData}
                      descriptionParams={qualityDescriptionParams}
                    />
                  )
                )}
                {view.dialogContent && <PlotDialogIcon onClick={handleOpenDialog} />}
                {!isFullFailure && downloadable && csvData && (
                  <PlotCsvDownload plotData={data} csvData={{ ...csvData, nameKey }} />
                )}
                {draggable && (
                  <DragIndicatorIcon
                    className="dragIcon"
                    data-testid="drag"
                    cursor="move"
                  />
                )}
              </Stack>
            </Box>

            {seriesLegend}

            <Box
              sx={
                view.type === "geo"
                  ? { height: "calc(100% - 2rem)" }
                  : { flex: 1, minHeight: 0, overflow: "hidden" }
              }
            >
              {isFullFailure ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    height: "100%",
                    minHeight: 0,
                    textAlign: "center",
                    px: 2,
                  }}
                  data-testid="plot-failed-state"
                >
                  <Typography variant="body1" color="text.secondary">
                    {t("error.COULD_NOT_LOAD_DATA")}
                  </Typography>
                </Box>
              ) : isEmptyInRange ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    minHeight: 0,
                    textAlign: "center",
                    px: 2,
                  }}
                  data-testid="plot-empty-range-state"
                >
                  <Typography variant="body2" color="text.secondary">
                    {t("generic.NO_DATA_IN_TIME_WINDOW")}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={
                    view.type === "geo"
                      ? { height: "100%", minHeight: 0 }
                      : { height: "100%", minHeight: 0 }
                  }
                >
                  {InnerPlot()}
                </Box>
              )}
            </Box>

            {attribution === "open-meteo" && <WeatherServiceAttribution />}
          </PlotContainer>

          {view.dialogContent && (
            <PopUp
              open={showDialog}
              onClose={handleCloseDialog}
              titleKey={view.dialogTitleKey ?? "plots_descriptions.DIAGRAM"}
              maxWidth="md"
              fullWidth
            >
              {view.dialogContent()}
            </PopUp>
          )}
        </>
      )}
    </>
  )
}
